/**
 * ============================================================================
 * MembershipsTable - Main Memberships Table Component
 * ============================================================================
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  CheckCircle,
  XCircle,
  ChevronDown,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  MoreVertical,
  Filter,
  X,
  Calendar,
  Users,
  Shield,
} from 'lucide-react';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import {
  formatCurrency,
  formatDuration,
  getStatusInfo,
  exportMembershipsToCSV,
} from '../../utils/membershipFormatters';
import type { MembershipPackage, MembershipFilters, MembershipStatus } from '../../types/memberships';

interface MembershipsTableProps {
  packages: MembershipPackage[];
  filters: MembershipFilters;
  onFilterChange: (filters: Partial<MembershipFilters>) => void;
  onAddPackage: () => void;
  onEditPackage: (pkg: MembershipPackage) => void;
  onDeletePackage: (id: number) => void;
  onToggleStatus: (id: number) => void;
  onSwitchToMembers: () => void;
}

function StatusBadge({ status }: { status: MembershipStatus }) {
  const { label, colorClass } = getStatusInfo(status);

  const icons = {
    active: CheckCircle,
    inactive: XCircle,
  };

  const Icon = icons[status];

  return (
    <span
      className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-bold ${colorClass}`}
      role="status"
      aria-label={`Status: ${label}`}
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      <span className="capitalize">{label}</span>
    </span>
  );
}

export function MembershipsTable({
  packages,
  filters,
  onFilterChange,
  onAddPackage,
  onEditPackage,
  onDeletePackage,
  onToggleStatus,
  onSwitchToMembers,
}: MembershipsTableProps) {
  const { confirm, alert } = useConfirmation();
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<MembershipStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilters]);

  // Tab filtering
  const tabFilteredPackages = packages.filter((pkg) => {
    if (activeTab === 'all') return true;
    return pkg.status === activeTab;
  });

  // Apply all filters
  const filteredPackages = tabFilteredPackages.filter((pkg) => {
    // Search filter
    if (filters.search && !pkg.name.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    // Duration type filter
    if (filters.durationType && filters.durationType !== 'all' && pkg.duration_type !== filters.durationType) {
      return false;
    }
    return true;
  });

  // Tab counts
  const tabCounts = {
    all: packages.length,
    active: packages.filter((p) => p.status === 'active').length,
    inactive: packages.filter((p) => p.status === 'inactive').length,
  };

  // Count active filters
  const activeFilterCount = [
    filters.durationType && filters.durationType !== 'all',
  ].filter(Boolean).length;

  // Delete handler with confirmation
  const handleDeleteClick = async (pkg: MembershipPackage) => {
    const memberCount = pkg.members_count || 0;
    const hasMembers = memberCount > 0;

    let message = `Are you sure you want to delete "${pkg.name}"?`;

    if (hasMembers) {
      message = `This package has ${memberCount} member${memberCount !== 1 ? 's' : ''}. You can only delete packages with no active members. Please deactivate the package instead or wait until all memberships expire.`;
    } else {
      message += '\n\nThis action cannot be undone.';
    }

    const confirmed = await confirm({
      title: hasMembers ? 'Cannot Delete Package' : 'Delete Package',
      message,
      confirmLabel: hasMembers ? 'Okay' : 'Delete',
      cancelLabel: hasMembers ? undefined : 'Cancel',
      variant: hasMembers ? 'warning' : 'danger',
    });

    // Only attempt delete if no members and user confirmed
    if (confirmed && !hasMembers) {
      onDeletePackage(pkg.id);
    }
  };

  return (
    <div>
      {/* Toolbar with View Mode and Search/Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* View Mode Buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              className="px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap bg-blue-600 text-white"
            >
              Packages
            </button>
            <button
              onClick={onSwitchToMembers}
              className="px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition-all whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Members
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-1 items-center gap-2">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search packages..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 w-full text-xs sm:text-sm"
                aria-label="Search packages"
              />
            </div>

            {/* Filter Dropdown */}
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
                  activeFilterCount > 0
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>

              {/* Filter Dropdown Content */}
              {showFilters && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-sm text-gray-900">Filters</h3>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Duration Type Filter */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Duration Type
                    </label>
                    <select
                      value={filters.durationType || 'all'}
                      onChange={(e) => onFilterChange({ durationType: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    >
                      <option value="all">All Types</option>
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => {
                        onFilterChange({ durationType: 'all' });
                        setShowFilters(false);
                      }}
                      className="w-full px-3 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="flex items-center overflow-x-auto border-b border-gray-200 px-3 sm:px-4">
          {[
            { id: 'all' as const, label: 'All Packages', shortLabel: 'All' },
            { id: 'active' as const, label: 'Active', shortLabel: 'Active' },
            { id: 'inactive' as const, label: 'Inactive', shortLabel: 'Inactive' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 sm:px-5 py-3 font-semibold text-xs sm:text-sm transition-all border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel}</span>
              {' '}<span className="text-gray-400">({tabCounts[tab.id]})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Package Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Duration
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Members
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Revenue
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredPackages.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                  {filters.search || (filters.durationType && filters.durationType !== 'all')
                    ? 'No packages match your filters'
                    : 'No packages found'}
                </td>
              </tr>
            ) : (
              filteredPackages.map((pkg) => (
                <React.Fragment key={pkg.id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setExpandedRow(expandedRow === pkg.id ? null : pkg.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-all"
                        aria-label={expandedRow === pkg.id ? 'Collapse details' : 'Expand details'}
                      >
                        <ChevronDown
                          className={`w-4 h-4 text-gray-600 transition-transform ${
                            expandedRow === pkg.id ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{pkg.name}</p>
                        <p className="text-xs text-gray-500">{pkg.description}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-black text-gray-900 text-base">
                        {formatCurrency(pkg.price)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1 whitespace-nowrap">
                        <Calendar className="w-3 h-3 text-gray-500" />
                        <span className="text-gray-700 text-sm font-medium">
                          {formatDuration(pkg.duration, pkg.duration_type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Users className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="font-bold text-gray-900 text-sm">
                          {pkg.members_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-black text-green-600 text-base">
                        {formatCurrency(pkg.total_revenue || 0)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={pkg.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => onEditPackage(pkg)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit package"
                          aria-label={`Edit ${pkg.name}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onToggleStatus(pkg.id)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title={pkg.status === 'active' ? 'Deactivate' : 'Activate'}
                          aria-label={`${pkg.status === 'active' ? 'Deactivate' : 'Activate'} ${pkg.name}`}
                        >
                          {pkg.status === 'active' ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(pkg)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            (pkg.members_count || 0) > 0
                              ? 'text-red-300 cursor-not-allowed'
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            (pkg.members_count || 0) > 0
                              ? `Cannot delete - ${pkg.members_count} active member${pkg.members_count !== 1 ? 's' : ''}`
                              : 'Delete package'
                          }
                          aria-label={`Delete ${pkg.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {expandedRow === pkg.id && (
                    <tr className="bg-blue-50 border-b border-blue-100">
                      <td colSpan={8} className="px-4 py-4">
                        <div className="ml-12">
                          <p className="text-xs font-bold text-gray-600 uppercase mb-3 flex items-center">
                            <Shield className="w-4 h-4 mr-2 text-blue-600" />
                            What's Included / Privileges ({pkg.facilities.length})
                          </p>
                          {pkg.facilities.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2">
                              {pkg.facilities.map((facility, idx) => (
                                <div key={idx} className="flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-sm text-gray-700">{facility}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              No facilities or privileges listed for this package
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination */}
        {tabFilteredPackages.length > 0 && (
          <div className="px-3 sm:px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              Showing <span className="font-semibold">{tabFilteredPackages.length}</span> of{' '}
              <span className="font-semibold">{packages.length}</span> packages
            </p>
            <div className="flex items-center justify-center space-x-2">
              <button className="px-2 sm:px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50">
                Prev
              </button>
              <button className="px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-semibold">
                1
              </button>
              <button className="px-2 sm:px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
